import {
  addPendingActionsService,
  createBankApplication,
  deleteBankApplication,
  getBankApplicationDetails,
  getBankApplicationsList,
  submitBankApplication,
  updateBankApplication,
  verifyApplicationExternalAccountsService,
} from '@/services/bankApplications';
import {
  BankApplicationFormValues,
  queryKeys,
  BankApplicationStatus,
  PendingActionsFormFields,
} from '@/types/bankApplications';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetBankApplicationCounts = () => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.list, 'count'],
    queryFn: () => getBankApplicationsList('statuses='),
    gcTime: Infinity,
    staleTime: Infinity,
  });

  return {
    aggregateInfo: data?.aggregateInfo,
    isBankApplicationsPending: isPending,
  };
};

export const useGetBankApplicationsList = (
  statuses: BankApplicationStatus[],
) => {
  const [searchParams] = useSearchParams();
  const params = searchParams.toString();
  const fullParams = params
    ? `${params}&statuses=${statuses}`
    : `statuses=${statuses}`;

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, fullParams],
    queryFn: () => getBankApplicationsList(fullParams),
  });

  return {
    isSuccess,
    bankApplications: data?.bankApplications?.records || [],
    aggregateInfo: data?.aggregateInfo,
    totalRecordCount: data?.bankApplications?.totalRecordCount || 0,
    currentPage: data?.bankApplications?.currentPage || 0,
    lastPage: data?.bankApplications?.lastPage || 0,
    isBankApplicationsPending: isPending,
  };
};

export const useGetBankApplicationDetails = (bankApplicationId: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.details, bankApplicationId],
    queryFn: () => getBankApplicationDetails(bankApplicationId),
    enabled: !!bankApplicationId,
  });

  return {
    bankApplication: data,
    isBankApplicationLoading: isPending,
  };
};

export const useCreateBankApplication = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: BankApplicationFormValues) =>
      createBankApplication({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    createBankApplication: mutateAsync,
    isCreateBankApplicationPending: isPending,
  };
};

export const useUpdateBankApplication = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: BankApplicationFormValues) =>
      updateBankApplication({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    updateBankApplication: mutateAsync,
    isUpdateBankApplicationPending: isPending,
  };
};

export const useSubmitBankApplication = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (bankApplicationId: string) =>
      submitBankApplication(bankApplicationId),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    submitBankApplication: mutateAsync,
    isSubmitBankApplicationPending: isPending,
  };
};

export const useDeleteBankApplication = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (bankApplicationId: string) =>
      deleteBankApplication(bankApplicationId),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    deleteBankApplication: mutateAsync,
    isDeleteBankApplicationPending: isPending,
  };
};

export const useAddPendingActions = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: PendingActionsFormFields) =>
      addPendingActionsService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addPendingActions: mutateAsync,
    isAddPendingActionsPending: isPending,
  };
};

export const useVerifyApplicationExternalAccounts = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => verifyApplicationExternalAccountsService(id),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    verifyApplicationExternalAccounts: mutateAsync,
    isVerifyApplicationExternalAccounts: isPending,
  };
};
